import styled from "@emotion/styled";
import { CSSProperties } from "react";
import palette from "../../../ui-lib/theme/palette";

type AlignOptions = "center" | "left" | "right";

interface TableProps {
  compact?: boolean;
}

export const StyledTable = styled("table")<TableProps>(({ theme }) => ({
  borderSpacing: 0,
  border: `1px  solid ${theme.border_color}`,
  borderRadius: "8px",
}));

interface HeaderProps {
  alternate?: boolean;
  compact?: boolean;
}

function getSizeStyles(size?: number) {
  if (typeof size !== "number") {
    size = 150;
  }

  return {
    flex: `${size} 0 auto`,
    minWidth: 0,
    width: size,
  };
}

export const HeaderRow = styled("tr")<HeaderProps>(({ alternate, theme }) => ({
  backgroundColor: theme.table_header_background_color,
  borderBottom: `1px solid ${theme.border_color}`,
  color: theme.text_color_inverse,
  display: "flex",
  fontSize: "0.75rem",
  height: "40px",
  borderRadius: "8px 8px 0 0",

  // NOTE: The order of these matters
  ...(alternate
    ? {
        ":nth-of-type(even) th": {
          backgroundColor: theme.background_color,
          borderRadius: "none",
        },
      }
    : {}),

  "th:first-of-type": {
    borderRadius: "8px 0 0 0",
  },

  "th:last-child": {
    borderRadius: "0 8px 0 0",
  },

  "th:only-child": {
    borderRadius: "8px 8px 0 0",
  },
}));

interface FooterProps {
  compact?: boolean;
  footerDelta?: boolean;
}

export const FooterRow = styled("tr")<FooterProps>(
  ({ footerDelta, theme }) => ({
    color: theme.text_color_inverse,
    fontSize: "0.75rem",
    height: footerDelta ? "60px" : "40px",
    display: "flex",
    flex: "1 0 auto",
    minWidth: 0,

    // NOTE: The order of these matters

    "td:first-of-type": {
      borderRadius: "0 0 0 8px",
    },

    "td:last-child": {
      borderRadius: "0 0 8px 0",
    },

    "td:only-child": {
      borderRadius: "0 0 8px 8px",
    },
  })
);

interface HeaderCellProps {
  align?: AlignOptions;
  sortable: boolean;
  left?: number;
  isPinned?: boolean;
  size?: number;
  zIndex?: number;
  borderStyle?: {
    border?: string;
    borderBottom?: string;
    borderLeft?: string;
    borderRight?: string;
    borderTop?: string;
  };
}

export const HeaderCell = styled("th")<HeaderCellProps>(
  ({ align, borderStyle, sortable, theme, left, isPinned, size, zIndex }) => ({
    backgroundColor: theme.table_header_background_color,
    ...(borderStyle ? borderStyle : {}),
    color: theme.text_color,
    fontWeight: "normal",
    padding: `${theme.space_sm} ${theme.space_lg}`,
    ...(sortable ? { cursor: "pointer" } : {}),
    ...(isPinned
      ? { position: "sticky", zIndex: zIndex ?? 1 }
      : { position: "relative" }),
    ...(typeof left === "number"
      ? { position: "sticky", left, zIndex: zIndex ?? 3 }
      : {}),
    ...getSizeStyles(size),

    "> div": {
      justifyContent: (() => {
        switch (align) {
          case "center":
            return "center";
          case "left":
            return "flex-start";
          case "right":
            return "flex-end";
        }
      })(),
    },
  })
);

interface RowProps {
  alternate?: boolean;
  clickable?: boolean;
  compact?: boolean;
  dndStyles?: CSSProperties;
  footer?: boolean;
  height?: string;
  isHighlighted?: boolean;
  isLastRow?: boolean;
}

export const StyledRow = styled("tr")<RowProps>(
  ({
    alternate,
    clickable,
    compact,
    dndStyles,
    theme,
    height,
    isHighlighted,
    isLastRow,
  }) => ({
    ...(clickable ? { cursor: "pointer" } : {}),
    fontSize: theme.fontSize_ui,
    height: height ? height : compact ? "36px" : "56px",
    minWidth: 0,
    display: "flex",
    flex: "1 0 auto",
    position: "relative",
    borderBottom: isLastRow ? "unset" : `1px solid ${theme.border_color}`,
    ...(alternate
      ? {
          ":nth-of-type(even) td": {
            backgroundColor: isHighlighted
              ? palette.blue[10]
              : theme.background_color,
          },
        }
      : {}),

    "td:first-of-type": {
      borderRadius: isLastRow ? "0 0 0 8px" : "unset",
    },

    "td:last-child": {
      borderRadius: isLastRow ? "0 0 8px 0" : "unset",
    },
    "&:hover > *": {
      ...(clickable
        ? { backgroundColor: theme.table_row_background_color_hover }
        : {}),
    },
    ...(dndStyles ? dndStyles : {}),
  })
);

interface CellProps {
  align?: AlignOptions;
  borderStyle?: {
    border?: string;
    borderBottom?: string;
    borderLeft?: string;
    borderRight?: string;
    borderTop?: string;
  };
  disabled?: boolean;
  footer?: boolean;
  isCellMasked?: boolean;
  isHighlighted?: boolean;
  isSelected: boolean;
  left?: number;
  size?: number;
}

export const StyledCell = styled("td")<CellProps>(
  ({
    align,
    borderStyle,
    disabled,
    isCellMasked,
    isHighlighted,
    isSelected,
    left,
    size,
    theme,
  }) => ({
    alignItems: "center",
    backgroundColor: isHighlighted
      ? palette.blue[10]
      : isSelected
        ? theme.table_cell_background_color_selected
        : theme.table_cell_background_color,

    color: isSelected
      ? theme.text_color_inverse
      : disabled
        ? theme.text_color_disabled
        : theme.text_color,
    display: "flex",
    justifyContent: (() => {
      switch (align) {
        case "center":
          return "center";
        case "left":
          return "flex-start";
        case "right":
          return "flex-end";
      }
    })(),
    padding: `${theme.space_sm} ${theme.space_lg}`,
    ...(borderStyle ? borderStyle : {}),
    ...getSizeStyles(size),

    ...(align && { textAlign: align }),
    ...(typeof left === "number"
      ? { position: "sticky", left, zIndex: 1 }
      : {}),

    ...(isCellMasked
      ? { backgroundColor: theme.table_cell_background_color_masked }
      : {}),
  })
);

export const FooterCell = styled("td")<CellProps>(
  ({ align, isSelected, theme, left, size }) => ({
    alignItems: "center",
    backgroundColor: theme.table_footer_background_color,
    color: isSelected ? theme.text_color_inverse : theme.text_color,
    display: "flex",
    ...getSizeStyles(size),
    fontSize: theme.fontSize_ui,
    justifyContent: (() => {
      switch (align) {
        case "center":
          return "center";
        case "left":
          return "flex-start";
        case "right":
          return "flex-end";
      }
    })(),
    padding: `${theme.space_sm} ${theme.space_lg}`,
    ...(align && { textAlign: align }),
    ...(typeof left === "number" ? { position: "sticky", left } : {}),
  })
);

export const StyledActionMenuButton = styled("button")(({ theme }) => ({
  backgroundColor: theme.panel_backgroundColor,
  border: `1px solid ${theme.secondary_color_border}`,
  borderRadius: theme.borderRadius_2,
  color: theme.text_color,
  cursor: "pointer",
  height: "2rem",
  outline: 0,
  padding: "0.375rem",
  width: "2rem",

  "&:hover": {
    backgroundColor: theme.secondary_color_background,
  },

  "&:focus": {
    backgroundColor: "#B5D7FF",
  },

  '& [role="img"]': {
    cursor: "pointer",
    height: "0.75rem",
    width: "0.75rem",
  },
}));
